import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement>;

const base = {
  'aria-hidden': true,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.7,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
} as const;

export const SearchIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <circle cx="8.75" cy="8.75" r="5.75" />
    <path d="m13.1 13.1 3.9 3.9" />
  </svg>
);

export const SortIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={1.9} {...p}>
    <path d="M4 6h12M6 10h8M8.5 14h3" />
  </svg>
);

export const ClearIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" aria-hidden {...p}>
    <circle cx="10" cy="10" r="8" fill="currentColor" />
    <path d="m7.2 7.2 5.6 5.6m0-5.6-5.6 5.6" stroke="var(--color-canvas)" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

export const ChevronLeftIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={2.1} {...p}>
    <path d="M12.5 4 6.5 10l6 6" />
  </svg>
);

export const ChevronRightIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={2.1} {...p}>
    <path d="m7.5 4 6 6-6 6" />
  </svg>
);

export const ChevronUpDownIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={1.9} {...p}>
    <path d="m6.5 8 3.5-3.5L13.5 8M6.5 12l3.5 3.5 3.5-3.5" />
  </svg>
);

export const CheckIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={2.2} {...p}>
    <path d="m4.5 10.5 3.6 3.6 7.4-8.2" />
  </svg>
);

export const CloseIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={2.1} {...p}>
    <path d="m5.5 5.5 9 9m0-9-9 9" />
  </svg>
);

export const ShareIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M10 2.75v10M6.5 6.25 10 2.75l3.5 3.5" />
    <path d="M6.75 8.75H5.5a1.75 1.75 0 0 0-1.75 1.75v5A1.75 1.75 0 0 0 5.5 17.25h9a1.75 1.75 0 0 0 1.75-1.75v-5a1.75 1.75 0 0 0-1.75-1.75h-1.25" />
  </svg>
);

export const DownloadIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M10 3v10M6 9.25l4 4 4-4M4 16.75h12" />
  </svg>
);

export const FileIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M11.5 2.75H6.25a1.5 1.5 0 0 0-1.5 1.5v11.5a1.5 1.5 0 0 0 1.5 1.5h7.5a1.5 1.5 0 0 0 1.5-1.5V6.5z" />
    <path d="M11.5 2.75V6.5h3.75M7.75 10.5h4.5M7.75 13.25h3" />
  </svg>
);

export const LinkIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M8.5 11.5a3.25 3.25 0 0 0 4.6 0l2.3-2.3a3.25 3.25 0 0 0-4.6-4.6l-.9.9" />
    <path d="M11.5 8.5a3.25 3.25 0 0 0-4.6 0l-2.3 2.3a3.25 3.25 0 0 0 4.6 4.6l.9-.9" />
  </svg>
);

export const LockIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <rect x="4.5" y="8.75" width="11" height="8.5" rx="2.25" />
    <path d="M7 8.75V6.5a3 3 0 0 1 6 0v2.25" />
  </svg>
);

export const ExpandIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M3.75 7.5V3.75H7.5M16.25 7.5V3.75H12.5M3.75 12.5v3.75H7.5M16.25 12.5v3.75H12.5" />
  </svg>
);

export const CollapseIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M7.5 3.75V7.5H3.75M12.5 3.75V7.5h3.75M7.5 16.25V12.5H3.75M12.5 16.25V12.5h3.75" />
  </svg>
);

export const MinusIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={2} {...p}>
    <path d="M5 10h10" />
  </svg>
);

export const PlusIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={2} {...p}>
    <path d="M10 5v10M5 10h10" />
  </svg>
);

export const ExternalIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M11.5 3.75h4.75V8.5M16 4l-7 7M14.25 11.5v3a1.75 1.75 0 0 1-1.75 1.75h-7A1.75 1.75 0 0 1 3.75 14.5v-7A1.75 1.75 0 0 1 5.5 5.75h3" />
  </svg>
);

export const ArrowUpRightIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={2.1} {...p}>
    <path d="M6 14 14 6M7.5 6H14v6.5" />
  </svg>
);

export const PlayIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" aria-hidden {...p}>
    <path
      d="M6 4.1v11.8a1.05 1.05 0 0 0 1.6.9l9.4-5.9a1.05 1.05 0 0 0 0-1.8L7.6 3.2A1.05 1.05 0 0 0 6 4.1Z"
      fill="currentColor"
    />
  </svg>
);

export const PauseIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" aria-hidden {...p}>
    <rect x="4.75" y="3.5" width="3.75" height="13" rx="1.1" fill="currentColor" />
    <rect x="11.5" y="3.5" width="3.75" height="13" rx="1.1" fill="currentColor" />
  </svg>
);

export const SpeakerIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={1.8} {...p}>
    <path d="M3.75 8v4h3l4 3.5v-11l-4 3.5h-3Z" fill="currentColor" />
    <path d="M13.5 7.25a3.9 3.9 0 0 1 0 5.5M15.6 5.2a6.8 6.8 0 0 1 0 9.6" />
  </svg>
);

export const SpeakerMutedIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} strokeWidth={1.8} {...p}>
    <path d="M3.75 8v4h3l4 3.5v-11l-4 3.5h-3Z" fill="currentColor" />
    <path d="m13.5 8 4 4m0-4-4 4" />
  </svg>
);

export const EyeIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M1.9 10.3a.8.8 0 0 1 0-.6C3.3 6.5 6.4 4.25 10 4.25s6.7 2.25 8.1 5.45a.8.8 0 0 1 0 .6c-1.4 3.2-4.5 5.45-8.1 5.45s-6.7-2.25-8.1-5.45Z" />
    <circle cx="10" cy="10" r="2.75" />
  </svg>
);

export const EyeOffIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M8.2 4.45A8.9 8.9 0 0 1 10 4.25c3.6 0 6.7 2.25 8.1 5.45a.8.8 0 0 1 0 .6 9.2 9.2 0 0 1-1.9 2.75M5.3 5.9a9.1 9.1 0 0 0-3.4 3.8.8.8 0 0 0 0 .6c1.4 3.2 4.5 5.45 8.1 5.45a8.8 8.8 0 0 0 4.3-1.1" />
    <path d="M8.1 8.1a2.75 2.75 0 0 0 3.8 3.8M3 3l14 14" />
  </svg>
);

export const AlertIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" aria-hidden {...p}>
    <circle cx="10" cy="10" r="8" fill="currentColor" />
    <path d="M10 5.75v5" stroke="var(--color-canvas)" strokeWidth="1.9" strokeLinecap="round" />
    <circle cx="10" cy="13.9" r="1.1" fill="var(--color-canvas)" />
  </svg>
);

export const SignOutIcon = (p: IconProps) => (
  <svg viewBox="0 0 20 20" {...base} {...p}>
    <path d="M11.75 4.25V3.5A1.75 1.75 0 0 0 10 1.75H5A1.75 1.75 0 0 0 3.25 3.5v13A1.75 1.75 0 0 0 5 18.25h5a1.75 1.75 0 0 0 1.75-1.75v-.75" />
    <path d="M8 10h9.25M14.25 7l3 3-3 3" />
  </svg>
);

export const EmptySearchIcon = (p: IconProps) => (
  <svg viewBox="0 0 48 48" {...base} strokeWidth={2.4} {...p}>
    <circle cx="21" cy="21" r="13" />
    <path d="m31 31 9 9M16.5 16.5l9 9m0-9-9 9" />
  </svg>
);

export const CloudOffIcon = (p: IconProps) => (
  <svg viewBox="0 0 48 48" {...base} strokeWidth={2.4} {...p}>
    <path d="M17 36h17a8 8 0 0 0 1.6-15.84A12 12 0 0 0 14.1 16.9 9.5 9.5 0 0 0 17 36Z" />
    <path d="m8 8 32 32" />
  </svg>
);
